import React, { useState, useEffect, useCallback, useRef } from 'react';
import { KpmgTransliterate } from '../../../TranslitrationSuggestion/KpmgTransliterate';
import { useAppContext } from "../../../../../context/AppContext";
import { useLogger } from '../../../../../utils/UnifiedLogger';
import useLocalization from '../../../../../hooks/useLocalization';
import { setIsHideUserInputFields } from '../../../../../store/features/message/textContentSlice';
import { useDispatch } from 'react-redux';
import PropTypes from "prop-types";
import { API_ENDPOINTS } from '../../../../../utils/constants';
import { createPortal } from "react-dom";
import ApiSearchDropdown from './ApiSearchDropdown';

const BotDynamicForm = ({
    field_form: initialFieldForm,
    formValues,
    setFormValues,
    errors,
    setErrors,
    setFormAnswers,
    setFilledForms,
    setDynamicFormSubmit,
    category_code,
    org_code
}) => {
    const firstLevelValueRef = useRef("");
    const dispatch = useDispatch();
    const { logInfo } = useLogger("BotDynamicForm", true);
    const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState(true);
    const [fieldForm, setFieldForm] = useState(initialFieldForm);
    const [searchResults, setSearchResults] = useState({});
    const [globalLoading, setGlobalLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [isInitialPrefillComplete, setIsInitialPrefillComplete] = useState(false);
    const [openDropdowns, setOpenDropdowns] = useState({});
    const [searchTerms, setSearchTerms] = useState({});

    const { t } = useLocalization();
    const { selectedLanguageData, sharedApiService } = useAppContext();
    const botAdditionalFieldsRequest = sharedApiService.sendRequest;

    const dropdownRefs = useRef({});
    const portalRefs = useRef({});

    // ✅ MOVED TO TOP LEVEL: Close dropdown on click outside
    useEffect(() => {
        const handler = (e) => {
            Object.keys(openDropdowns).forEach(fieldName => {
                if (openDropdowns[fieldName]) {
                    const clickedMain = dropdownRefs.current[fieldName]?.contains(e.target);
                    const clickedPortal = portalRefs.current[fieldName]?.contains(e.target);

                    if (!clickedMain && !clickedPortal) {
                        setOpenDropdowns(prev => ({ ...prev, [fieldName]: false }));
                    }
                }
            });
        };

        const hasOpenDropdown = Object.values(openDropdowns).some(Boolean);
        if (hasOpenDropdown) {
            document.addEventListener("mousedown", handler);
            return () => document.removeEventListener("mousedown", handler);
        }
    }, [openDropdowns]);

    useEffect(() => {
        if (!initialFieldForm) return;

        let updatedForm = { ...initialFieldForm };
        const newInputFields = [...(updatedForm.InputFields || [])];
        const newDropDown = [...(updatedForm.DropDown || [])];

        newInputFields.forEach((field, idx) => {
            if (field.HasChilds === true) {
                const nextField = newInputFields[idx + 1];
                const isNextFieldChild = nextField && nextField.FieldOrder === field.FieldOrder + 1;

                if (!isNextFieldChild) {
                    const childField = {
                        ...field,
                        FieldName: `${field.FieldName}_child`,
                        DisplayLable: `Additional Fields`,
                        FieldOrder: field.FieldOrder + 1,
                        IsMandatory: false,
                        options: [],
                        HasChilds: false
                    };

                    newInputFields.splice(idx + 1, 0, childField);

                    const dropdownFieldsCount = newInputFields.filter(f => f.FieldType?.toLowerCase() === 'dropdown').length;
                    newDropDown.push([]);
                }
            }
        });

        newInputFields.sort((a, b) => a.FieldOrder - b.FieldOrder);

        updatedForm.InputFields = newInputFields;
        updatedForm.DropDown = newDropDown;

        setFieldForm(updatedForm);
    }, [initialFieldForm]);

    const isFieldFormContainsData = () =>
        fieldForm && Array.isArray(fieldForm.InputFields) && fieldForm.InputFields.length > 0;

    const extractValueFromResponse = (field, dropdownOptions = []) => {
        if (!field.response) return null;

        const allOptions = [
            ...(field.options || []),
            ...(dropdownOptions || [])
        ];

        const matchedOption = allOptions.find(opt =>
            opt.Text === field.response ||
            opt.Text?.toLowerCase() === field.response?.toLowerCase() ||
            opt.Value === field.response ||
            opt.Value?.toLowerCase() === field.response?.toLowerCase()
        );

        return matchedOption ? matchedOption.Value : null;
    };

    const getDropdownIndexByFieldName = (fieldName) => {
        const dropdownFields = fieldForm.InputFields.filter(f =>
            f.FieldType?.toLowerCase() === 'dropdown'
        );
        return dropdownFields.findIndex(f => f.FieldName === fieldName);
    };

    const callAdditionalFieldsAPI = async (fieldObj, value, isFromPrefill = false, prefillChildValue = null) => {
        logInfo('CALLING_ADDITIONAL_FIELDS_API', 'Calling API for additional fields', {
            fieldName: fieldObj.FieldName,
            parentValue: value,
            isFromPrefill,
            prefillChildValue
        });

        setGlobalLoading(true);
        setApiError(null);

        try {
            const sortedFields = [
                ...(fieldForm.InputFields?.filter(
                    f => f.FieldType?.toLowerCase() === 'dropdown'
                ) || [])
            ].sort((a, b) => a.FieldOrder - b.FieldOrder);
            // First field
            const firstLevelField = sortedFields?.[0];

            // Base/initial firstLevelValue logic
            let firstLevelValue =
                firstLevelField
                    ? firstLevelField.FieldType.toLowerCase() === 'dropdown'
                        ? formValues[firstLevelField.FieldName]
                        : firstLevelValueRef.current
                    : firstLevelValueRef.current;

            // Dropdown fields (excluding child)
            const dropdownFields =
                sortedFields?.filter(
                    f => f.FieldType.toLowerCase() === 'dropdown' && !f.FieldName.toLowerCase().includes('child')
                ) || [];

            /**
             * ⭐ RULE:
             * If CURRENT field is the FIRST field → use parentValue
             */
            if (fieldObj.FieldName === firstLevelField?.FieldName && firstLevelField.FieldType.toLowerCase() === 'dropdown') {
                firstLevelValue = value;
            }

            await botAdditionalFieldsRequest({
                method: "GET",
                url: `${API_ENDPOINTS.getAdditionalDropdownValueList}?language=${selectedLanguageData?.code}&orgCode=${org_code}&firstLevelInputFieldDropdownValue=${firstLevelValue}&parentDropdownFieldName=${fieldObj.FieldName}&parentDropdownValue=${value}&currentCategory=${category_code}`,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `CPGRAMS Q0hCT1Q6VEhZR0JINzY4OTBKSFlBV1Na...`
                },
                abortable: true,
                isEncrypted: false,

                onSuccess: (res) => {
                    setGlobalLoading(false);

                    if (!Array.isArray(res) || res.length === 0) return;

                    const formattedOptions = res
                        .map(item => {
                            const isEnglish = selectedLanguageData?.code?.toLowerCase() === "en";
                            const regionalText = typeof item.Value_regional === "string"
                                ? item.Value_regional.replace(/^\[?'?/, '').replace(/'?\]?$/, '').trim()
                                : item.Value_regional;
                            const displayText = isEnglish
                                ? (item.Value || item.text || item.name || "")
                                : (regionalText || item.Value || "");

                            const isSelected = prefillChildValue &&
                                (item.Key === prefillChildValue || item.Value === prefillChildValue);

                            return {
                                Value: item.Key || item.Value || "",
                                Text: displayText,
                                Disabled: false,
                                Selected: isSelected,
                                isSelected: isSelected
                            };
                        })
                        .filter(opt => opt.Value !== "" && opt.Text !== "");

                    logInfo('ADDITIONAL_FIELD_OPTIONS_LOADED', 'Child options loaded from API', {
                        fieldName: fieldObj.FieldName,
                        parentValue: value,
                        optionsCount: formattedOptions.length
                    });

                    setFieldForm(prev => {
                        const updatedForm = { ...prev };

                        const parentDropdownIndex = getDropdownIndexByFieldName(fieldObj.FieldName);

                        const childDropdownIndex = parentDropdownIndex + 1;

                        if (updatedForm.DropDown && updatedForm.DropDown[childDropdownIndex]) {
                            updatedForm.DropDown[childDropdownIndex] = formattedOptions;
                        }

                        updatedForm.InputFields = updatedForm.InputFields.map((f, idx) => {
                            if (idx === childDropdownIndex) {
                                return {
                                    ...f,
                                    options: formattedOptions,
                                    IsHidden: false
                                };
                            }
                            return f;
                        });

                        return updatedForm;
                    });
                },

                onError: (err) => {
                    console.log("Error fetching additional fields:", err);
                    setGlobalLoading(false);
                    if (!isFromPrefill) {
                        setApiError("Failed to load dependent fields. Please retry.");
                    }
                },
            });
        } catch (error) {
            console.error("Exception in fetching additional fields:", error);
            setGlobalLoading(false);
            if (!isFromPrefill) {
                setApiError("Something went wrong. Please retry.");
            }
        }
    };

    useEffect(() => {
        if (!isFieldFormContainsData() || isInitialPrefillComplete) return;

        const prefilled = {};
        const apiCallsToMake = [];

        setFieldForm(prev => {
            const updatedForm = { ...prev };
            updatedForm.InputFields = updatedForm.InputFields.map(field => ({
                ...field,
                IsHidden: false
            }));
            return updatedForm;
        });

        if (initialFieldForm.field_form_list && Array.isArray(initialFieldForm.field_form_list)) {
            initialFieldForm.field_form_list.forEach(fieldInfo => {
                if (fieldInfo.UserInputValue || fieldInfo.UserInputText) {
                    const value = fieldInfo.UserInputValue || fieldInfo.UserInputText;
                    prefilled[fieldInfo.FieldName] = value;

                    if (fieldInfo.UserInputChildValue) {
                        prefilled[`${fieldInfo.FieldName}_child`] = fieldInfo.UserInputChildValue;
                    }

                    const field = initialFieldForm.InputFields?.find(f => f.FieldName === fieldInfo.FieldName);

                    if (field && (field.HasChilds || fieldInfo.UserInputChildValue) && value) {
                        apiCallsToMake.push({
                            field,
                            value,
                            childValue: fieldInfo.UserInputChildValue,
                            order: field.FieldOrder
                        });
                    }
                }
            });
        }

        initialFieldForm.InputFields?.forEach((field) => {
            if (prefilled[field.FieldName]) return;

            let valueToSet = null;

            if (field.UserInputValue !== null && field.UserInputValue !== undefined) {
                valueToSet = field.UserInputValue;
            }
            else if (field.UserInputText !== null && field.UserInputText !== undefined) {
                valueToSet = field.UserInputText;
            }
            else if (field.response) {
                if (field.FieldType?.toLowerCase() === 'dropdown') {
                    const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
                    const dropdownOptions = initialFieldForm.DropDown?.[dropdownIndex] || [];
                    valueToSet = extractValueFromResponse(field, dropdownOptions);

                    logInfo('PREFILL_RESPONSE_EXTRACTED', 'Extracted VALUE from response', {
                        fieldName: field.FieldName,
                        responseText: field.response,
                        extractedValue: valueToSet
                    });
                } else {
                    valueToSet = field.response;
                }
            }
            else if (field.FieldValue) {
                valueToSet = field.FieldValue;
            }

            if (valueToSet !== null) {
                prefilled[field.FieldName] = valueToSet;

                if (field.UserInputChildValue) {
                    prefilled[`${field.FieldName}_child`] = field.UserInputChildValue;
                }

                if (field.FieldOrder === 1) {
                    firstLevelValueRef.current = valueToSet;
                }

                if (field.HasChilds || field.UserInputChildValue) {
                    apiCallsToMake.push({
                        field,
                        value: valueToSet,
                        childValue: field.UserInputChildValue,
                        order: field.FieldOrder
                    });
                }
            }
        });

        logInfo('PREFILL_VALUES', 'Setting pre-filled values', {
            prefilled,
            apiCallsToMake: apiCallsToMake.length
        });

        if (Object.keys(prefilled).length > 0) {
            setFormValues(prev => ({ ...prev, ...prefilled }));
        }

        if (apiCallsToMake.length > 0) {
            const sortedCalls = apiCallsToMake.sort((a, b) => a.order - b.order);
            sortedCalls.forEach(({ field, value, childValue }) => {
                callAdditionalFieldsAPI(field, value, true, childValue);
            });
        }

        setIsInitialPrefillComplete(true);

    }, [initialFieldForm, setFormValues, logInfo, isInitialPrefillComplete]);

    const handleChange = (field, value) => {
        logInfo('FIELD_VALUE_CHANGED', 'Field value changed', {
            fieldName: field.FieldName,
            newValue: value
        });
        const fieldName = field.FieldName;

        setFormValues(prev => ({ ...prev, [fieldName]: value }));
        setErrors(prev => ({ ...prev, [fieldName]: '' }));

        if (field.FieldOrder === 1) {
            firstLevelValueRef.current = value;
            logInfo('FIRST_LEVEL_DROPDOWN_CHANGED', 'First level dropdown value updated', {
                fieldName,
                newValue: value
            });
        }

        if (field.HasChilds) {
            const currentOrder = field.FieldOrder;

            // Find only dropdown children
            const childDropdownFields = fieldForm.InputFields.filter(
                f => f.FieldOrder > currentOrder && f.FieldType?.toLowerCase() === "dropdown"
            );

            // Clear only dropdown values
            setFormValues(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => delete updated[child.FieldName]);
                return updated;
            });

            // Reset only dropdown option lists + hide them
            setFieldForm(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => {
                    const idx = getDropdownIndexByFieldName(child.FieldName);
                    if (idx !== -1) updated.DropDown[idx] = [];

                    updated.InputFields = updated.InputFields.map(f =>
                        f.FieldName === child.FieldName
                            ? { ...f, options: [], IsHidden: true }
                            : f
                    );
                });
                return updated;
            });

            // Close dropdown UI
            setOpenDropdowns(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => (updated[child.FieldName] = false));
                return updated;
            });

            // Clear search terms for dropdowns only
            setSearchTerms(prev => {
                const updated = { ...prev };
                childDropdownFields.forEach(child => (updated[child.FieldName] = ""));
                return updated;
            });

            // Fetch next-level dropdowns
            if (value) {
                callAdditionalFieldsAPI(field, value, false);
            }
        }

    };

    const handleTextChange = useCallback(() => { }, []);

    const validateField = (field) => {
        const value = formValues[field.FieldName] || '';
        if (field.IsMandatory && !value) {
            return t('requiredField') || `${selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional} is required`;
        }

        return '';
    };

    const getDropdownOptions = (field) => {
        const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
        const options = fieldForm.DropDown?.[dropdownIndex] || [];

        return options.filter(opt => opt.Value !== "" || opt.Text !== "");
    };

    const isFieldEnabled = (field) => {
        if (!isSubmitButtonVisible) return false;

        if (field.FieldOrder > 1) {
            const parentField = fieldForm.InputFields.find(f =>
                f.FieldOrder === field.FieldOrder - 1 && f.HasChilds
            );

            if (parentField && !formValues[parentField.FieldName]) {
                return false;
            }
        }

        return true;
    };

    /** ✅ Render dropdown field - NO useEffect inside! */
    const renderDropdown = (field) => {
        const dropdownOptions = getDropdownOptions(field);
        const isEnabled = isFieldEnabled(field);
        const currentValue = formValues[field.FieldName] || "";
        const isRegional = selectedLanguageData?.code?.toLowerCase() !== "en";
        const isOpen = openDropdowns[field.FieldName] || false;
        const searchTerm = searchTerms[field.FieldName] || "";

        const selectedOption = dropdownOptions.find(opt =>
            opt.Value?.toString() === currentValue?.toString()
        );

        const displayValue = selectedOption
            ? (isRegional
                ? (selectedOption.Text_regional || selectedOption.Text)
                : selectedOption.Text)
            : "";

        const filteredOptions = dropdownOptions.filter(option => {
            const displayText = isRegional
                ? (option.Text_regional || option.Text || "")
                : (option.Text || "");
            return displayText.toLowerCase().includes(searchTerm.toLowerCase());
        });

        const handleSelect = (value) => {
            handleChange(field, value);
            setOpenDropdowns(prev => ({ ...prev, [field.FieldName]: false }));
            setSearchTerms(prev => ({ ...prev, [field.FieldName]: "" }));
        };

        const getPortalStyle = () => {
            if (!dropdownRefs.current[field.FieldName]) return {};

            const rect = dropdownRefs.current[field.FieldName].getBoundingClientRect();
            const approxHeight = 240;

            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;

            const top = spaceAbove > approxHeight && spaceBelow < approxHeight
                ? rect.top - approxHeight - 8
                : rect.bottom + 8;

            return {
                left: rect.left,
                top,
                width: rect.width
            };
        };

        return (
            <div key={field.FieldName} className="relative w-full">
                <div
                    ref={el => (dropdownRefs.current[field.FieldName] = el)}
                    className={`border rounded-md px-3 py-2 ${isEnabled ? "cursor-pointer bg-white" : "bg-gray-100 cursor-not-allowed"} flex justify-between items-center`}
                    onClick={() => isEnabled && setOpenDropdowns(prev => ({ ...prev, [field.FieldName]: !isOpen }))}
                >
                    <span className={!currentValue ? "text-gray-400" : ""}>
                        {displayValue || "Select..."}
                    </span>

                    <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 9l-7 7-7-7" />
                    </svg>
                </div>

                {isOpen && isEnabled && createPortal(
                    <div
                        ref={el => (portalRefs.current[field.FieldName] = el)}
                        className="fixed z-[9999] bg-white border rounded-md shadow-2xl max-h-60 overflow-auto"
                        style={getPortalStyle()}
                    >
                        <div className="sticky top-0 bg-white p-2 border-b">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerms(prev => ({ ...prev, [field.FieldName]: e.target.value }))}
                                className="w-full px-2 py-1 border rounded text-sm"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>

                        <div className="max-h-48 overflow-y-auto">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, i) => (
                                    <div
                                        key={opt.Value}
                                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${opt.Value === currentValue ? "bg-blue-100" : ""}`}
                                        onClick={() => !opt.Disabled && handleSelect(opt.Value)}
                                    >
                                        {isRegional
                                            ? (opt.Text_regional || opt.Text)
                                            : opt.Text}
                                    </div>
                                ))
                            ) : (
                                <div className="p-3 text-center text-gray-500">No options found</div>
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    };

    const handleTextChange_text = useCallback(() => { }, []);

    function getLocalizedDropdownText(field, combinedOptions, selectedLanguageData) {
        const langCode = selectedLanguageData?.code?.toLowerCase() || "en";
        const isRegional = langCode !== "en";

        let matchedOption = combinedOptions.find(opt => opt.Value === field.UserInputValue);

        if (!matchedOption && field.UserInputText) {
            matchedOption = combinedOptions.find(opt => {
                const text = (opt.Text || "").trim().toLowerCase();
                const textRegional = (opt.Text_regional || "")
                    .replace(/(^'+)|('+$)/g, "")
                    .trim()
                    .toLowerCase();
                const input = field.UserInputText.trim().toLowerCase();
                return text === input || textRegional === input;
            });
        }

        if (matchedOption) {
            const textEnglish = (matchedOption.Text || "").trim();
            const textRegional = (matchedOption.Text_regional || "")
                .replace(/(^'+)|('+$)/g, "")
                .trim();

            return isRegional
                ? (textRegional || textEnglish || field.UserInputText || "")
                : (textEnglish || field.UserInputText || "");
        }

        return field.UserInputText || field.UserInputValue || "";
    }

    function filterRegionalValues(submissionPayload, selectedLanguageData) {
        if (!submissionPayload?.field_form?.InputFields) return {};
        const filtered = {};
        const dropdownArrays = submissionPayload?.field_form?.DropDown || [];
        const mandatoryDropdowns = submissionPayload?.field_form?.mandatory_dropdown_options || {};

        submissionPayload.field_form.InputFields.forEach((field, index) => {
            const fieldName = field.FieldName;
            const fieldType = field.FieldType?.toLowerCase();
            let value = "";

            const combinedOptions = [
                ...(field.options || []),
                ...(dropdownArrays[index] || []),
                ...(mandatoryDropdowns[fieldName] || []),
            ];

            switch (fieldType) {
                case "dropdown":
                case "select":
                    value = getLocalizedDropdownText(field, combinedOptions, selectedLanguageData);
                    break;

                default:
                    value = field.UserInputValue || "";
                    break;
            }

            filtered[fieldName] = value;
        });

        return filtered;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        fieldForm.InputFields.forEach(field => {
            const error = validateField(field);
            if (error) newErrors[field.FieldName] = error;
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const orderedAnswers = {};
        fieldForm.InputFields.forEach(field => {
            const fieldName = field.FieldName;
            const fieldType = field.FieldType?.toLowerCase();
            const userValue = formValues[fieldName];

            if (fieldType === "dropdown") {
                const dropdownOptions = getDropdownOptions(field);
                const selectedOption = dropdownOptions.find(opt => opt.Value === userValue);
                orderedAnswers[fieldName] = selectedOption?.Text || userValue || null;
            } else {
                orderedAnswers[fieldName] = userValue || null;
            }
        });

        const filledForm = JSON.parse(JSON.stringify(fieldForm));
        filledForm.InputFields = filledForm.InputFields.map(field => {
            const fieldName = field.FieldName;
            const fieldType = field.FieldType?.toLowerCase();
            const userValue = formValues[fieldName];

            if (fieldType === "dropdown") {
                const dropdownOptions = getDropdownOptions(field);
                const selectedOption = dropdownOptions.find(opt => opt.Value === userValue);

                return {
                    ...field,
                    UserInputValue: selectedOption?.Value || userValue || null,
                    UserInputText: selectedOption?.Text || userValue || null,
                };
            } else {
                return {
                    ...field,
                    UserInputValue: userValue || null
                };
            }
        });

        const submissionPayload = {
            field_form: filledForm,
            submitted_at: new Date().toISOString(),
        };

        const finalAnswersData = filterRegionalValues(submissionPayload, selectedLanguageData);

        logInfo('FORM_SUBMIT_COMPLETE', 'Complete filled form with UserInputValue/Text', {
            submissionPayload,
            orderedAnswers,
            finalAnswersData
        });
        setFormAnswers(finalAnswersData);
        setFilledForms(submissionPayload);
        setDynamicFormSubmit(true);
        setIsSubmitButtonVisible(false);
        dispatch(setIsHideUserInputFields(false));
    };

    const renderField = (field) => {
        const fieldType = field.FieldType?.toLowerCase() || 'text';
        const isEnabled = isFieldEnabled(field);

        if (fieldType === "dropdown") {
            if (field.IsSearchable) {
                return (
                    <ApiSearchDropdown
                        field={field}
                        onChange={handleChange}
                    />
                )
            } else {
                return renderDropdown(field);
            }
        }

        if (fieldType === 'textarea') {
            return (
                <textarea
                    value={formValues[field.FieldName] || ""}
                    onChange={(e) => handleChange(field, e.target.value)}
                    disabled={!isEnabled}
                    placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                    className="border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full disabled:bg-gray-100"
                    rows={4}
                />
            );
        }

        if (fieldType === 'checkbox' || fieldType === 'radio') {
            return (
                <input
                    type={fieldType}
                    checked={!!formValues[field.FieldName]}
                    onChange={(e) => handleChange(field, e.target.checked)}
                    disabled={!isEnabled}
                    className="w-4 h-4 mt-2"
                />
            );
        }


        if (fieldType === 'number') {
            return (
                <input
                    type="number"
                    value={formValues[field.FieldName] ?? ""}
                    onChange={(e) => {
                        const val = e.target.value;

                        if (val === "") {
                            // allow the field to stay empty
                            handleChange(field, "");
                            return;
                        }

                        const num = Number(val);
                        // clamp negatives but do NOT force non-empty to 0
                        handleChange(field, num < 0 ? 0 : num);
                    }}
                    min="0"
                    disabled={!isEnabled}
                    placeholder={
                        selectedLanguageData?.code === "en"
                            ? field.DisplayLable
                            : field.DisplayLable_regional
                    }
                    className="border rounded-md px-3 py-2 focus:outline-none focus:ring-1 
                               focus:ring-blue-500 w-full disabled:bg-gray-100"
                />
            );
        }

        if (fieldType === 'textbox' || fieldType === 'text') {
            return (
                <KpmgTransliterate
                    type="text"
                    lang={selectedLanguageData?.code || 'en'}
                    className="border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full disabled:bg-gray-100"
                    value={formValues[field.FieldName] || ''}
                    placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                    onChange={(e) => handleChange(field, e.target.value)}
                    disabled={!isEnabled}
                    enabled={selectedLanguageData?.code !== "en"}
                    onChangeText={(e) => handleTextChange_text(field.FieldName, e.value)}
                />
            );
        }

        return (
            <input
                type={fieldType}
                value={formValues[field.FieldName] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
                disabled={!isEnabled}
                placeholder={selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full disabled:bg-gray-100"
            />
        );
    };

    if (!isFieldFormContainsData()) return null;

    const sortedFields = [...fieldForm.InputFields].sort((a, b) => a.FieldOrder - b.FieldOrder);

    return (
        <div className='relative'>

            {globalLoading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg z-20">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mb-3"></div>
                        <p className="text-gray-700 text-base font-medium">
                            {t('loadingData') || 'Loading, please wait...'}
                        </p>
                    </div>
                </div>
            )}

            {apiError && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg z-20">
                    <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-sm">
                        <p className="text-red-600 text-base mb-3 font-medium">{apiError}</p>
                        <button
                            type="button"
                            onClick={() => setApiError(null)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                        >
                            {t('retry') || 'Retry'}
                        </button>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="mt-2">
                {sortedFields.map((field) => {
                    return (
                        <div key={field.FieldName} className="flex flex-col mb-3">
                            <label className="text-sm font-medium mb-1">
                                {selectedLanguageData?.code === "en" ? field.DisplayLable : field.DisplayLable_regional}{" "}
                                {field.IsMandatory && <span className="text-red-500">*</span>}
                            </label>

                            {renderField(field)}

                            {errors[field.FieldName] && (
                                <p className="text-sm text-red-600 mt-1">{errors[field.FieldName]}</p>
                            )}
                        </div>
                    );
                })}

                {isSubmitButtonVisible && (
                    <button
                        type="submit"
                        className="bg-[#14478E] text-white px-7 py-2 rounded-md mt-1 hover:bg-blue-700 transition"
                    >
                        {t('submit') || 'Submit'}
                    </button>
                )}
            </form>
        </div>
    );
};

BotDynamicForm.propTypes = {
    field_form: PropTypes.object,
    formValues: PropTypes.object,
    setFormValues: PropTypes.func,
    errors: PropTypes.object,
    setErrors: PropTypes.func,
    setFormAnswers: PropTypes.func,
    setFilledForms: PropTypes.func,
    setDynamicFormSubmit: PropTypes.func,
    category_code: PropTypes.string,
    org_code: PropTypes.string
};

export default BotDynamicForm;